import { useState } from "react";

import { Button } from "../ui/button";

/**
 * The paste-code box for the authorize-URL sign-in shape (Claude).
 *
 * Claude's CLI prints an authorize URL and then BLOCKS on stdin until the code
 * the browser handed back is written to it — unlike Codex, which prints a
 * device code and polls the provider itself. A surface that renders no way to
 * send that code leaves the process waiting until the session expires. See
 * `providerAuthExpectsPastedCode` in `@cmmd-forge/contracts`, which is the one
 * place that decides which shape a session is.
 */
export function PasteCodeForm({
  submitCode,
}: {
  readonly submitCode: (code: string) => Promise<void>;
}) {
  const [pastedCode, setPastedCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const trimmedCode = pastedCode.trim();

  // Delivery failures are reported through the PHASE, not here: committing
  // locks submission and unmounts this form, so a form-local error would be
  // discarded exactly when there is finally something to say. See
  // cloudProviderSignIn's `submitError`. This still awaits so the button stays
  // busy for the round trip, and swallows the rejection so it is not an
  // unhandled promise.
  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await submitCode(trimmedCode);
    } catch {
      // Reported on the phase.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!trimmedCode || submitting) return;
          void submit();
        }}
      >
        <input
          aria-label="Paste your sign-in code"
          className="h-8 flex-1 rounded border border-border/70 bg-background px-2 font-mono text-sm text-foreground"
          placeholder="Paste code"
          value={pastedCode}
          onChange={(event) => setPastedCode(event.target.value)}
        />
        <Button type="submit" size="sm" className="h-8" disabled={!trimmedCode || submitting}>
          {submitting ? "Connecting…" : "Connect"}
        </Button>
      </form>
    </>
  );
}
